import { ServiceProvider } from "Service/ServiceProvider";
import { BaseResult, ControlDefinition, Environment } from "Service/Client";
import * as jstz from "jstimezonedetect";
import { ApplicationParameter } from "./ApplicationParameter";
import { TextBlockCategory } from "Enums";
import { App, CustomBinding, Navigation } from "Core";
import { IControl } from "Controls/Interfaces";
import { Configuration } from "Configuration";

/**
 * @hidden
 */
export class AppContext {
    /**
     * @hidden
     */
    public mappingName: string;

    /**
     * @hidden
     */
    public initialTextBlockCategory: string;

    /**
     * @hidden
     */
    public currentTextBlockCategory: string;

    /**
     * @hidden
     */
    // public static serviceProviderInstance: ServiceProvider = new ServiceProvider();

    /**
     * @hidden
     */
    public serviceProvider: ServiceProvider;

    public static current: AppContext;

    public environment: Environment;

    public appViewModel: kendo.data.ObservableObject;

    constructor() {
        AppContext.current = this;
        this.serviceProvider = ServiceProvider.current;

        this.appViewModel = kendo.observable({
            TextBlock: {},
            menuLogoPath: "",
            menuLogoPathSmall: "",
            showEditTextBlock: false,
            breadcrumbs: [],
        });
    }

    async initialize(): Promise<void> {
        CustomBinding.initialize();

        const environment = await this.serviceProvider.Environment.get();
        this.configure(environment);

        this.appViewModel.set("menuLogoPath", environment.LogoPath);
        this.appViewModel.set("menuLogoPathSmall", environment.LogoPathSmall);
        this.appViewModel.set("showEditTextBlock", environment.HasTextBlockButton);
        this.appViewModel.set("localLoginEnabled", environment.IsLocalLoginEnabled);
        this.appViewModel.set("localLogoutEnabled", environment.IsLocalLoginEnabled && !Configuration.isDmsConfig);

        let textBlockCategories = [TextBlockCategory.userData, TextBlockCategory.global, "menu"];
        this.serviceProvider.TextBlock.load(this.environment.LanguageCode, textBlockCategories.toString()).then((textblocks) => {
            this.appViewModel.set("TextBlock", textblocks);
        });

        const userDataTextBlocks = [
            "ToolTipSetStartPage",
            "ToolTipGoToStartPage",
            "ToolTipEditTextBlocks",
            "ToolTipNotifications",
            "Logout",
            "ToolTipLogout",
            "ToolTipGlobalSearch",
            "GlobalSearchPlaceholder",
            "ChangePassword",
        ];

        for (const textblock of userDataTextBlocks) {
            (<any>this.appViewModel)[`getUserDataTextBlock${textblock}`] = () => {
                this.appViewModel.get("TextBlock");
                return this.serviceProvider.TextBlock.get(textblock, TextBlockCategory.userData);
            };
        }
    }

    /**
     * Configures the Context with the Environment.
     * @param environment Environment.
     * @hidden
     */
    public configure(environment: Environment): void {
        this.environment = environment;

        // initialize both cultures
        kendo.culture("de");
        kendo.culture("en-US");

        if (environment.Culture) {
            kendo.culture(environment.Culture);
            kendo.cultures["current"].numberFormat[","] = environment.NumberFormatGroupSeparator;
            kendo.cultures["current"].numberFormat["."] = environment.NumberFormatCommaSeparator;
        }
        let tz = jstz.determine();
        environment.ClientTimeZone = tz.name();

        ApplicationParameter.initialize(environment.ApplicationParameters);

        if (!environment.EnablePageContentTextBlock) {
            $("body").addClass("dms-hide-pagecontent");
        }
    }

    /**
     * Initializes control based on ControlDefinition and Configuration.
     * @param controlDefinition
     * @param textBlockCategory
     * @hidden
     */
    public initializeControl(controlDefinition: ControlDefinition, textBlockCategory: string, environment: Environment): IControl {
        let controlTypeFactory = Configuration.getControlFactory(controlDefinition.ControlTypeID);
        if (controlTypeFactory != null) {
            return controlTypeFactory(controlDefinition, textBlockCategory, this.serviceProvider, environment);
        }
        console.error("factory not found");
        return null;
    }

    /**
     * Validates Selector for JQuery-Element.
     * @param selector
     * @hidden
     */
    public validateElement(selector: string | JQuery): JQuery {
        if (typeof selector === "string") {
            return $(selector);
        } else if (selector instanceof jQuery) {
            // JQuery
            return selector;
        } else {
            throw new Error("Invalid jQuery selector!");
        }
    }

    /**
     * Calculate the available height for the content area.
     * @hidden
     */
    public getAvailableHeight(): number {
        let positionTop = 0;
        let windowHeight = $(window).height();

        // if (this.mapping && this.mapping.getGrids().length > 0) {
        //     positionTop = this.mapping.getGrids()[0].getOuterElement().offset().top;
        // }
        return windowHeight - positionTop;
    }

    /**
     * @hidden
     * @param result
     */
    public static handleServiceError(result: any) {
        if (result) {
            // user logged out and is unauthorized now.
            if (result.status == 401) {
                this.current.serviceProvider.PageToken.stopPolling();
                Navigation.errorAsync(
                    this.current.serviceProvider.TextBlock.get("SessionExpired", TextBlockCategory.global),
                    "Session"
                ).then(() => {
                    App.current.auth.logout();
                });
            } else if (result.status == 500) {
                let obj = JSON.parse(result.response);
                return this.handleServiceResult(obj);
            } else {
                this.current.serviceProvider.PageToken.stopPolling();
            }
        }
    }

    /**
     * @hidden
     * @param result
     */
    public static handleServiceResult<T extends BaseResult>(result: T): T {
        if (result && result.ErrorTextBlockName) {
            Navigation.errorAsync(
                this.current.serviceProvider.TextBlock.get(
                    result.ErrorTextBlockName,
                    result.ErrorTextBlockCategory ?? this.current.currentTextBlockCategory
                ),
                "Error"
            ).then(() => {
                if (result.ErrorTextBlockName == "PageExpired") {
                    window.location.reload();
                }
            });
            console.log(result.ErrorDetail);
        } else if (result && result.Error) {
            let errorMessage = result.Error;
            if (result.ErrorID) {
                errorMessage += "<br/><br/>Error ID: " + result.ErrorID;
            }
            Navigation.errorAsync(errorMessage, "Error");
            console.log(result.ErrorDetail);
        }

        if (result && ((<any>result).isSessionExpired || result.IsSessionExpired)) {
            this.current.serviceProvider.PageToken.stopPolling();
            Navigation.errorAsync(this.current.serviceProvider.TextBlock.get("SessionExpired", TextBlockCategory.global), "Session").then(
                () => {
                    App.current.auth.logout();
                }
            );
            return result;
        }

        return result;
    }
}
